import React, { useState, useEffect } from 'react';
import api from '../../api/axios';
import { getToken } from '../../utils/auth';
import { toast, ToastContainer } from 'react-toastify';
import {
  BarChart,
  Bar,
  XAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  LineChart,
  Line,
  YAxis,
  Legend,
} from 'recharts';
import 'react-toastify/dist/ReactToastify.css';

const FinanzasAdmin = () => {
  const [periodo, setPeriodo] = useState('mensual');
  const [mes, setMes] = useState(new Date().getMonth() + 1);
  const [año, setAño] = useState(new Date().getFullYear());
  const [datos, setDatos] = useState(null);
  const [evolucion, setEvolucion] = useState([]);
  const [tabActiva, setTabActiva] = useState('mes');

  const cargarFinanzas = async () => {
    const token = getToken();
    try {
      const params = { periodo, año };
      if (periodo === 'mensual') params.mes = mes;

      const res = await api.get(`/admin/finanzas`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });

      setDatos(res.data);

      // Cargar evolución solo en vista mensual
      if (periodo === 'mensual') {
        const resEvolucion = await api.get(`/admin/finanzas/evolucion`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { año, mes }
        });
        setEvolucion(resEvolucion.data.evolucion || []);
      }
    } catch (err) {
      console.error('❌ Error al cargar finanzas:', err);
      toast.error('Error al cargar los datos financieros');
    }
  };

  useEffect(() => {
    cargarFinanzas();
  }, [periodo, mes, año]);

  const meses = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const resumenChart = datos
    ? Object.values(datos.resumenServicios).map(servicio => ({
        nombre: servicio.nombre,
        Ingresos: servicio.total
      }))
    : [];

  const renderComparativa = (valor) => {
    if (valor === null || valor === undefined) return 'Sin datos';
    const esPositivo = parseFloat(valor) >= 0;
    const clase = esPositivo ? 'positivo' : 'negativo';
    return <span className={clase}>{esPositivo ? '+' : ''}{valor}%</span>;
  };

  return (
    <main className="finanzas-admin site-width">
      <div className="texto-dashboard">
        <h1>Panel de Finanzas</h1>
        <p>Consulta los ingresos de tu negocio por mes o año, y visualiza los servicios que más aportan.</p>
      </div>

      {!datos ? (
        <p className="loader">Cargando finanzas...</p>
      ) : (
        <>
          <div className="datos-total-preview">
            <div className="filtros-finanzas">
              <h3>Periodo</h3>
              <p className='texto-datos'>Selecciona el período a consultar</p>
              <div className="selectores-finanzas">
                <select value={periodo} onChange={e => setPeriodo(e.target.value)}>
                  <option value="mensual">Mensual</option>
                  <option value="anual">Anual</option>
                </select>

                {periodo === 'mensual' && (
                  <select value={mes} onChange={e => setMes(Number(e.target.value))}>
                    {meses.map((mesNombre, index) => (
                      <option key={index} value={index + 1}>
                        {mesNombre}
                      </option>
                    ))}
                  </select>
                )}

                <input
                  type="number"
                  value={año}
                  onChange={e => setAño(Number(e.target.value))}
                  min="2020"
                  max={new Date().getFullYear() + 5}
                />
              </div>
            </div>

            <div className="datos-total-ingresos">
              <h3>Total Ingresos</h3>
              <p className="fecha-datos">{periodo === 'mensual' ? `${meses[mes - 1]} ${año}` : año}</p>
              <p className='valor-dato'>{datos.totalIngresos.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</p>
            </div>

            <div className="datos-total-citas">
              <h3>Total Citas</h3>
              <p className="fecha-datos">{periodo === 'mensual' ? `${meses[mes - 1]} ${año}` : año}</p>
              <p className='valor-dato'>{datos.totalCitas}</p>
            </div>

            <div className="datos-total-ingresos">
              <h3>Ingreso Promedio</h3>
              <p className="fecha-datos">{periodo === 'mensual' ? `${meses[mes - 1]} ${año}` : año}</p>
              <p className='valor-dato'>{datos.ingresoPromedio?.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' }) || '0 €'}</p>
            </div>

            <div className="datos-total-citas">
              <h3>Tasa de No-Asistencias</h3>
              <p className="fecha-datos">{periodo === 'mensual' ? `${meses[mes - 1]} ${año}` : año}</p>
              <p className='valor-dato'>{datos.tasaNoAsistencias || 0}%</p>
            </div>
          </div>

          {periodo === 'mensual' && evolucion.length > 0 && (
            <div className="grafico-finanzas grafico-evolucion">
              <h3>Evolución de Ingresos {año}</h3>
              <p>Todos los meses del año</p>
              <ResponsiveContainer width="100%" height={300} style={{ marginTop: '20px' }}>
                <LineChart data={evolucion}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="mes" />
                  <YAxis />
                  <Tooltip formatter={(value) => `${value}€`} />
                  <Legend />
                  <Line type="monotone" dataKey="ingresos" stroke="#2563eb" strokeWidth={2} name="Ingresos" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="grafico-finanzas">
            <h3>Ingresos por Servicio</h3>
            <p>Desglose de ingresos por tipo de servicio</p>
            <ResponsiveContainer width="100%" height={300} style={{ marginTop: '20px' }}>
              <BarChart data={resumenChart}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="nombre" />
                <Tooltip formatter={(value) => `${value}€`} />
                <Bar dataKey="Ingresos" fill="#2563eb" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="datos-extra">
            <div className="resumen-servicios">
              <h4>Ingresos por Servicio:</h4>
              <div className="resumen-servicio-grid">
                {Object.values(datos.resumenServicios).map(servicio => (
                    <div key={servicio.nombre} className="servicio-finanza">
                    <p><strong>{servicio.nombre}</strong>: {servicio.cantidad} citas – {servicio.total.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</p>
                    </div>
                ))}
                </div>
            </div>
            <div className="tabs-comparativas">
              <h3>Comparativa</h3>
              <div className="tab-buttons">
                <button
                  className={tabActiva === 'mes' ? 'activo' : ''}
                  onClick={() => setTabActiva('mes')}
                >
                  Vs. Mes anterior
                </button>
                <button
                  className={tabActiva === 'año' ? 'activo' : ''}
                  onClick={() => setTabActiva('año')}
                >
                  Vs. Año anterior
                </button>
              </div>

              <div className="tab-content">
                {tabActiva === 'mes' && (
                  <div className="comparativa-mensual">
                    <p>Ingresos: {renderComparativa(datos.comparativas?.mensual?.ingresos)}</p>
                    <p>Citas: {renderComparativa(datos.comparativas?.mensual?.citas)}</p>
                  </div>
                )}
                {tabActiva === 'año' && (
                  <div className="comparativa-anual">
                    <p>Ingresos: {renderComparativa(datos.comparativas?.anual?.ingresos)}</p>
                    <p>Citas: {renderComparativa(datos.comparativas?.anual?.citas)}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="pagos-plataforma">
            <h3>Pagos a la Plataforma</h3>
            <p>Visualiza los pagos realizados a la plataforma</p>

            {/* Cálculo de comisiones */}
            <div className="resumen-servicio-grid">
                {Object.entries(datos.resumenServicios).map(([idServicio, servicio]) => {
                let comision = 0;

                if (parseInt(idServicio) === 2) {
                    comision = 0.80;
                } else if (parseInt(idServicio) === 1) {
                    comision = 0.40;
                } else if (parseInt(idServicio) === 3) {
                    comision = 0.20;
                }

                const pagoServicio = servicio.cantidad * comision;

                return (
                    <div key={idServicio} className="servicio-finanza">
                    <p>
                        <strong>{servicio.nombre}</strong>: {servicio.cantidad} citas × {comision.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })} =
                        <strong> {pagoServicio.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</strong>
                    </p>
                    </div>
                );
                })}
            </div>

            {/* Cálculo del total */}
            <div className="total-pagos-plataforma">
                <h4>Total a pagar:</h4>
                <p>
                {(() => {
                  const totalCalculado = Object.entries(datos.resumenServicios).reduce((total, [idServicio, servicio]) => {
                    let comision = 0;
                    if (parseInt(idServicio) === 2) {
                      comision = 0.80;
                    } else if (parseInt(idServicio) === 1) {
                      comision = 0.40;
                    } else if (parseInt(idServicio) === 3) {
                      comision = 0.20;
                    }
                    return total + servicio.cantidad * comision;
                  }, 0);
                  
                  return (
                    <>
                      {totalCalculado.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                      {" "}/ 65 €
                    </>
                  );
                })()}
                </p>
                {Object.entries(datos.resumenServicios).reduce((total, [idServicio, servicio]) => {
                  let comision = 0;
                  if (parseInt(idServicio) === 2) {
                    comision = 0.80;
                  } else if (parseInt(idServicio) === 1) {
                    comision = 0.40;
                  } else if (parseInt(idServicio) === 3) {
                    comision = 0.20;
                  }
                  return total + servicio.cantidad * comision;
                }, 0) > 65 && (
                  <p className="aviso-maximo" >
                    ✨ Superaste el límite establecido, solo pagarás 65€. ¡Enhorabuena!
                  </p>
                )}
            </div>
            </div>

          </div>
        </>
      )}

      <ToastContainer position="bottom-right" autoClose={3000} />
    </main>
  );
};

export default FinanzasAdmin;
